import { useSyncExternalStore, useRef, useCallback } from 'react';

/**
 * Shallow compare deux objets (compare les valeurs au premier niveau)
 */
export const shallow = <T extends object>(objA: T, objB: T): boolean => {
  if (Object.is(objA, objB)) return true;
  if (
    typeof objA !== 'object' ||
    objA === null ||
    typeof objB !== 'object' ||
    objB === null
  ) {
    return false;
  }

  const keysA = Object.keys(objA) as (keyof T)[];
  const keysB = Object.keys(objB) as (keyof T)[];

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(objB, key)) return false;
    if (!Object.is(objA[key], objB[key])) return false;
  }

  return true;
};

type Listener = () => void;
type SetState<T> = (partial: Partial<T> | ((state: T) => Partial<T>)) => void;
type GetState<T> = () => T;
type Subscribe = (listener: Listener) => () => void;

type StoreApi<T> = {
  getState: GetState<T>;
  setState: SetState<T>;
  subscribe: Subscribe;
  destroy: () => void;
};

type StateCreator<T> = (
  set: SetState<T>,
  get: GetState<T>,
  api: StoreApi<T>
) => T;

/**
 * Crée un store externe à React
 *
 * @example
 * const useCounterStore = createStore((set, get) => ({
 *   count: 0,
 *   increment: () => set({ count: get().count + 1 }),
 *   decrement: () => set({ count: get().count - 1 }),
 * }));
 *
 * // Dans un composant
 * const count = useCounterStore(state => state.count);
 * const { increment } = useCounterStore(state => ({ increment: state.increment }));
 */
export const createStore = <T extends object>(
  initializer: StateCreator<T>
): (<U>(selector: (state: T) => U, equalityFn?: (a: U, b: U) => boolean) => U) & StoreApi<T> => {
  let state: T;
  const listeners = new Set<Listener>();

  const getState: GetState<T> = () => state;

  const setState: SetState<T> = partial => {
    const nextState =
      typeof partial === 'function'
        ? (partial as (state: T) => Partial<T>)(state)
        : partial;

    if (!Object.is(nextState, state)) {
      state = { ...state, ...nextState };
      listeners.forEach(listener => listener());
    }
  };

  const subscribe: Subscribe = listener => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const destroy = () => {
    listeners.clear();
  };

  const api: StoreApi<T> = { getState, setState, subscribe, destroy };

  // Initialise le state
  state = initializer(setState, getState, api);

  // Hook pour utiliser le store dans les composants
  const useStore = <U>(
    selector: (state: T) => U,
    equalityFn: (a: U, b: U) => boolean = Object.is
  ): U => {
    const selectorRef = useRef(selector);
    const equalityFnRef = useRef(equalityFn);
    const selectedStateRef = useRef<U>();

    selectorRef.current = selector;
    equalityFnRef.current = equalityFn;

    const getSelection = useCallback(() => {
      const nextSelectedState = selectorRef.current(state);

      if (
        selectedStateRef.current !== undefined &&
        equalityFnRef.current(selectedStateRef.current, nextSelectedState)
      ) {
        return selectedStateRef.current;
      }

      selectedStateRef.current = nextSelectedState;
      return nextSelectedState;
    }, []);

    return useSyncExternalStore(subscribe, getSelection, getSelection);
  };

  // Attache les méthodes de l'API au hook
  Object.assign(useStore, api);

  return useStore as typeof useStore & StoreApi<T>;
};

/**
 * Crée un store avec un reducer (pattern Redux-like)
 *
 * @example
 * const useStore = createStoreWithReducer(reducer, initialState, {
 *   // Actions optionnelles
 *   fetchData: async (dispatch, getState) => {
 *     dispatch({ type: 'LOADING' });
 *     const data = await api.getData();
 *     dispatch({ type: 'SET_DATA', payload: data });
 *   }
 * });
 */
export const createStoreWithReducer = <
  TState extends object,
  TAction extends { type: string },
  TAsyncActions extends Record<string, (...args: any[]) => Promise<any> | void> = Record<string, never>
>(
  reducer: (state: TState, action: TAction) => TState,
  initialState: TState,
  asyncActionsCreator?: (
    dispatch: (action: TAction) => void,
    getState: () => TState
  ) => TAsyncActions
): (<U>(selector: (state: TState) => U, equalityFn?: (a: U, b: U) => boolean) => U) &
  StoreApi<TState> & {
    dispatch: (action: TAction) => void;
    actions: TAsyncActions;
  } => {
  let state = initialState;
  const listeners = new Set<Listener>();

  const getState = () => state;

  const setState = (partial: Partial<TState>) => {
    state = { ...state, ...partial };
    listeners.forEach(listener => listener());
  };

  const dispatch = (action: TAction) => {
    const nextState = reducer(state, action);
    if (!Object.is(nextState, state)) {
      state = nextState;
      listeners.forEach(listener => listener());
    }
  };

  const subscribe: Subscribe = listener => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const destroy = () => {
    listeners.clear();
  };

  const api = { getState, setState, subscribe, destroy };

  // Crée les async actions
  const actions = asyncActionsCreator
    ? asyncActionsCreator(dispatch, getState)
    : ({} as TAsyncActions);

  // Hook pour utiliser le store
  const useStore = <U>(
    selector: (state: TState) => U,
    equalityFn: (a: U, b: U) => boolean = Object.is
  ): U => {
    const selectorRef = useRef(selector);
    const equalityFnRef = useRef(equalityFn);
    const selectedStateRef = useRef<U>();

    selectorRef.current = selector;
    equalityFnRef.current = equalityFn;

    const getSelection = useCallback(() => {
      const nextSelectedState = selectorRef.current(state);

      if (
        selectedStateRef.current !== undefined &&
        equalityFnRef.current(selectedStateRef.current, nextSelectedState)
      ) {
        return selectedStateRef.current;
      }

      selectedStateRef.current = nextSelectedState;
      return nextSelectedState;
    }, []);

    return useSyncExternalStore(subscribe, getSelection, getSelection);
  };

  // Attache les méthodes au hook
  Object.assign(useStore, api, { dispatch, actions });

  return useStore as typeof useStore & StoreApi<TState> & {
    dispatch: (action: TAction) => void;
    actions: TAsyncActions;
  };
};
